import { useState } from "react";
import { useParams } from "react-router-dom";
import { Newspaper, Search, ScrollText, Users } from "lucide-react";
import { useFetch } from "@/lib/api";
import { usePageMeta, PageHeader, EmptyState, Loader, ErrorState, Pagination, BackLink } from "@/components/common";
import { NewsCard } from "@/pages/Home";
import { NEWS_CAT, ROLE_LABEL, fmtDate, fmtDateTime } from "@/lib/format";
import { ClanApplicationForm } from "@/components/ClanApplicationForm";

export default function News() {
  usePageMeta("News", "Comunicazioni ufficiali e annunci di NEXUM LEGACY.");
  const [f, setF] = useState({ page: 1 });
  const { data, loading, error, reload } = useFetch("/news", f);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v || undefined, page: 1 }));
  return (
    <div data-testid="news-page">
      <PageHeader eyebrow="Comunicazioni" title="News" subtitle="Annunci, comunicazioni dello staff, risultati e novità dell'alleanza." />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-wrap gap-2.5 mb-8">
          <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#C6C6CE]/40" /><input data-testid="news-search" placeholder="Cerca notizia" className="nx-input pl-9" onChange={(e) => set("q", e.target.value)} /></div>
          <select data-testid="news-filter-category" aria-label="Categoria" className="nx-input w-auto" onChange={(e) => set("category", e.target.value)}><option value="">Tutte le categorie</option>{Object.entries(NEWS_CAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        </div>
        {error ? <ErrorState message={error} onRetry={reload} /> : loading && !data ? <Loader /> : data.items.length ? (
          <><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.items.map((n) => <NewsCard key={n.id} n={n} />)}</div><Pagination page={data.page} pages={data.pages} onChange={(p) => setF((x) => ({ ...x, page: p }))} /></>
        ) : <EmptyState icon={Newspaper} title="Nessuna notizia pubblicata" />}
      </div>
    </div>
  );
}

export function NewsDetail() {
  const { slug } = useParams();
  const { data: n, loading, error, reload } = useFetch(`/news/${slug}`);
  usePageMeta(n?.title, n?.excerpt);
  if (loading && !n) return <div className="pt-24"><Loader /></div>;
  if (error) return <div className="max-w-3xl mx-auto px-5 pt-32"><ErrorState message={error} onRetry={reload} /></div>;
  return (
    <article data-testid="news-detail-page" className="max-w-3xl mx-auto px-5 md:px-8 pt-28 pb-10">
      <BackLink to="/news" label="News" />
      <div className="mt-8 flex items-center gap-3 text-[11px] uppercase tracking-widest"><span className="text-[#962FFF]">{NEWS_CAT[n.category]}</span><span className="text-[#C6C6CE]/50">{fmtDateTime(n.publish_at)}</span><span className="text-[#C6C6CE]/50">· {n.author_name}</span></div>
      <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-[#F5F5F7]">{n.title}</h1>
      {n.image_url && <img src={n.image_url} alt="" className="mt-8 w-full rounded-md border border-[#C6C6CE]/10 object-cover max-h-[460px]" />}
      <div className="mt-8 prose-nx" data-testid="news-content">{n.content}</div>
    </article>
  );
}

export function Alliance() {
  usePageMeta("L'alleanza", "Presentazione, missione, valori e procedura di adesione a NEXUM LEGACY.");
  const { data, loading, error, reload } = useFetch("/site");
  const pages = (data?.pages || []);
  return (
    <div data-testid="alliance-page">
      <PageHeader eyebrow="Istituzionale" title="L'alleanza" subtitle="Chi siamo, come lavoriamo e come entrare a far parte di NEXUM LEGACY." />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        {error ? <ErrorState message={error} onRetry={reload} /> : loading ? <Loader /> : (
          <div className="grid lg:grid-cols-4 gap-10">
            <nav className="hidden lg:block sticky top-24 self-start space-y-2" aria-label="Sezioni">
              {pages.map((p) => <a key={p.key} href={`#${p.key}`} className="block text-xs uppercase tracking-widest text-[#C6C6CE]/60 hover:text-[#F5F5F7] transition-colors duration-200">{p.title}</a>)}
              <a href="#candidati" data-testid="alliance-apply-anchor" className="block text-xs uppercase tracking-widest text-[#962FFF] hover:text-[#F5F5F7] transition-colors duration-200">Candida il tuo clan</a>
            </nav>
            <div className="lg:col-span-3 space-y-6">
              {pages.map((p, i) => (
                <section key={p.key} id={p.key} data-testid={`alliance-section-${p.key}`} className="nx-card p-6 md:p-10 scroll-mt-24">
                  <div className="font-num text-xs text-[#962FFF] mb-2">{String(i + 1).padStart(2, "0")}</div>
                  <h2 className="text-xl md:text-2xl font-bold uppercase text-[#F5F5F7] mb-4">{p.title}</h2>
                  <p className="prose-nx">{p.content || "Contenuto in fase di redazione da parte dello staff."}</p>
                </section>
              ))}
              <div id="candidati" className="scroll-mt-24"><ClanApplicationForm /></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function Staff() {
  usePageMeta("Staff e organizzazione");
  const { data, loading, error, reload } = useFetch("/staff");
  const org = useFetch("/site");
  const orgPage = org.data?.pages?.find((p) => p.key === "organizzazione");
  return (
    <div data-testid="staff-page">
      <PageHeader eyebrow="Organizzazione" title="Staff" subtitle={orgPage?.content?.slice(0, 220) || "Il team che organizza e gestisce le competizioni dell'alleanza."} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        {error ? <ErrorState message={error} onRetry={reload} /> : loading ? <Loader /> : data.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.map((s) => (
              <div key={s.id} data-testid={`staff-card-${s.id}`} className="nx-card p-6">
                <div className="w-12 h-12 rounded-md bg-[#17171D] border border-[#962FFF]/30 flex items-center justify-center font-display font-bold text-[#d6b3ff]">{(s.display_name || "?").slice(0, 2).toUpperCase()}</div>
                <div className="mt-4 font-display font-bold uppercase text-[#F5F5F7]">{s.display_name}</div>
                {s.gamertag && <div className="font-num text-xs text-[#C6C6CE]/50">{s.gamertag}</div>}
                <div className="mt-3 text-xs text-[#962FFF] uppercase tracking-widest">{s.public_title || ROLE_LABEL[s.role]}</div>
              </div>
            ))}
          </div>
        ) : <EmptyState icon={Users} title="Nessun membro dello staff" />}
      </div>
    </div>
  );
}

export function Regulations() {
  usePageMeta("Regolamenti", "Regolamenti ufficiali delle competizioni NEXUM LEGACY.");
  const { data, loading, error, reload } = useFetch("/regulations");
  return (
    <div data-testid="regulations-page">
      <PageHeader eyebrow="Ufficiale" title="Regolamenti" subtitle="Regole generali e specifiche applicate alle competizioni." />
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 space-y-6">
        {error ? <ErrorState message={error} onRetry={reload} /> : loading ? <Loader /> : data.length ? data.map((r) => (
          <section key={r.id} id={r.slug} data-testid={`regulation-${r.slug}`} className="nx-card p-6 md:p-10 scroll-mt-24">
            <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-widest"><span className="text-[#962FFF]">{r.category}</span><span className="text-[#C6C6CE]/50 font-num">v{r.version} · aggiornato {fmtDate(r.updated_at)}</span></div>
            <h2 className="mt-3 text-xl md:text-2xl font-bold uppercase text-[#F5F5F7]">{r.title}</h2>
            <p className="mt-4 prose-nx">{r.content}</p>
          </section>
        )) : <EmptyState icon={ScrollText} title="Nessun regolamento pubblicato" />}
      </div>
    </div>
  );
}
